import { useState, useEffect } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { HeaderOptionsPopover } from "@/components/HeaderOptionsPopover";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Settings,
  User,
  MessageCircle,
  DollarSign,
  Handshake,
  Edit,
  Crown,
  Phone,
  Bell,
  CheckCircle,
  XCircle,
  Download,
  Trash2,
  Shield,
  Database,
  Activity,
  Ban,
  CalendarDays,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Pricing } from "@/components/Pricing";
import type { Database as SupabaseDatabase } from "@/integrations/supabase/types";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { sub } from "date-fns";
import { AutoTranslate } from "@/lib/autoTranslate.tsx";
import {
  buildScheduledDowngradeMessage,
  formatSubscriptionEndDate,
  hasActiveProAccess,
  isSubscriptionExpired,
} from "@/lib/subscription";
import { SEO } from "@/components/SEO";

export default function SettingsPage() {
  const transText = useTranslation().t("autoTranslate", {
    returnObjects: true,
  });
  const { i18n } = useTranslation();

  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [yearsExperience, setYearsExperience] = useState(0);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newCountryCode, setNewCountryCode] = useState("");
  const [newJobTitle, setNewJobTitle] = useState("");
  const [newYearsExperience, setNewYearsExperience] = useState([0]);
  const [notifications, setNotifications] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [commentsHidden, setCommentsHidden] = useState(false);
  const [commentNotificationsEnabled, setCommentNotificationsEnabled] =
    useState(true);
  const [commentNotificationFrequency, setCommentNotificationFrequency] =
    useState("instant");
  const [subscriptionTier, setSubscriptionTier] = useState("starter");
  const [loading, setLoading] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [subscriptionCanceled, setSubscriptionCanceled] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<Date | null>(null);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (window as any).Tally?.loadEmbeds?.();
  }, []);

  const subscriptionStatus = AutoTranslate(`Subscription Status`);
  const notificationFrequency = AutoTranslate(`Notification Frequency`);
  // Type for profile settings data
  type ProfileSettings = Pick<
    SupabaseDatabase["public"]["Tables"]["profiles"]["Row"],
    | "full_name"
    | "phone_number"
    | "country_code"
    | "job_title"
    | "years_experience"
    | "notifications_enabled"
    | "comments_enabled"
    | "comments_hidden"
    | "comment_notifications_enabled"
    | "comment_notification_frequency"
    | "subscription_tier"
    | "subscription_end"
    | "subscription_canceled"
    | "city"
    | "created_at"
  >;

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      setEmailVerified(user.email_confirmed_at !== null);
    }
  }, [user]);

  const fetchUserProfile = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("profiles")
      .select(
        `full_name, phone_number, country_code, job_title, years_experience,
           notifications_enabled, comments_enabled, comments_hidden,
           comment_notifications_enabled, comment_notification_frequency,
           subscription_tier, subscription_canceled, subscription_end, city, created_at`,
      )
      .eq("user_id", user.id)
      .single();

    const profile = data as ProfileSettings | null;

    if (data) {
      const nextSubscriptionEnd = data.subscription_end
        ? new Date(data.subscription_end)
        : null;
      const shouldMoveToFree =
        data.subscription_tier === "pro" &&
        Boolean(data.subscription_end) &&
        isSubscriptionExpired(data.subscription_end);

      if (shouldMoveToFree) {
        await supabase
          .from("profiles")
          .update({
            subscription_tier: "starter",
            subscription_status: "expired",
            subscription_canceled: false,
            subscription_end: null,
          })
          .eq("user_id", user.id);
      }

      setFullName(data.full_name || "");
      setPhoneNumber(data.phone_number || "");
      setCountryCode(data.country_code || "+1");
      setJobTitle(data.job_title || "");
      setYearsExperience(data.years_experience || 0);
      setNewName(data.full_name || "");
      setNewPhone(data.phone_number || "");
      setNewCountryCode(data.country_code || "+1");
      setNewJobTitle(data.job_title || "");
      setNewYearsExperience([data.years_experience || 0]);
      setNotifications(data.notifications_enabled ?? true);
      setCommentsEnabled(data.comments_enabled ?? true);
      setCommentsHidden(data.comments_hidden ?? false);
      setCommentNotificationsEnabled(
        data.comment_notifications_enabled ?? true,
      );
      setCommentNotificationFrequency(
        data.comment_notification_frequency || "instant",
      );
      setSubscriptionTier(shouldMoveToFree ? "starter" : data.subscription_tier || "starter");
      setSubscriptionEnd(shouldMoveToFree ? null : nextSubscriptionEnd);
      setSubscriptionCanceled(shouldMoveToFree ? false : data.subscription_canceled ?? false);
    }
  };

  console.log("Subscription Canceled:", subscriptionCanceled);

  const handleNameUpdate = async () => {
    if (!user || !newName.trim()) return;

    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: newName.trim() })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate("Failed to update name. Please try again."),
        variant: "destructive",
      });
    } else {
      setFullName(newName.trim());
      setShowNameDialog(false);
      toast({
        title: AutoTranslate("Name Updated"),
        description: AutoTranslate("Your name has been updated successfully."),
      });
    }
    setLoading(false);
  };

  const handleProfileUpdate = async () => {
    if (!user) return;

    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        phone_number: newPhone.trim(),
        country_code: newCountryCode,
        job_title: newJobTitle.trim(),
        years_experience: newYearsExperience[0],
      })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate(
          "Failed to update profile. Please try again.",
        ),
        variant: "destructive",
      });
    } else {
      setPhoneNumber(newPhone.trim());
      setCountryCode(newCountryCode);
      setJobTitle(newJobTitle.trim());
      setYearsExperience(newYearsExperience[0]);
      setShowProfileDialog(false);
      toast({
        title: AutoTranslate("Profile Updated"),
        description: AutoTranslate(
          "Your profile has been updated successfully.",
        ),
      });
    }
    setLoading(false);
  };

  const handleNotificationToggle = async (enabled: boolean) => {
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update({ notifications_enabled: enabled })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update notification settings.",
        variant: "destructive",
      });
    } else {
      setNotifications(enabled);
      if (!enabled) {
        toast({
          title: "Unsubscribed",
          description:
            "You've unsubscribed, you'll stop receiving updates to your email",
          variant: "default",
        });
      } else {
        toast({
          title: "Subscribed",
          description: "You'll now receive email updates and notifications.",
        });
      }
    }
  };

  const handleCommentsEnabledToggle = async (enabled: boolean) => {
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update({ comments_enabled: enabled })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate("Failed to update comment settings."),
        variant: "destructive",
      });
    } else {
      setCommentsEnabled(enabled);
      toast({
        title: enabled
          ? AutoTranslate("Comments Enabled")
          : AutoTranslate("Comments Disabled"),
        description: enabled
          ? AutoTranslate("Users can now comment on your shared resumes.")
          : AutoTranslate(
              "Comments are now disabled on all your shared resumes.",
            ),
      });
    }
  };

  const handleCommentsHiddenToggle = async (hidden: boolean) => {
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update({ comments_hidden: hidden })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate("Failed to update comment visibility."),
        variant: "destructive",
      });
    } else {
      setCommentsHidden(hidden);
      toast({
        title: hidden
          ? AutoTranslate("Comments Hidden")
          : AutoTranslate("Comments Visible"),
        description: hidden
          ? AutoTranslate("Comments are now hidden from others.")
          : AutoTranslate("Comments are now visible to everyone."),
      });
    }
  };

  const handleCommentNotificationsToggle = async (enabled: boolean) => {
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update({ comment_notifications_enabled: enabled })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate("Failed to update comment notifications."),
        variant: "destructive",
      });
    } else {
      setCommentNotificationsEnabled(enabled);
      toast({
        title: enabled
          ? "Comment Notifications Enabled"
          : "Comment Notifications Disabled",
        description: enabled
          ? "You will receive email notifications for new comments."
          : "You will not receive email notifications for new comments.",
      });
    }
  };

  const handleCommentNotificationFrequencyChange = async (
    frequency: string,
  ) => {
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update({ comment_notification_frequency: frequency })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate("Failed to update notification frequency."),
        variant: "destructive",
      });
    } else {
      setCommentNotificationFrequency(frequency);
      toast({
        title: AutoTranslate("Notification Frequency Updated"),
        description: `${AutoTranslate("Comment notifications will be sent")} ${frequency}.`,
      });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke(
        "cancel-subscription",
      );
      if (error) throw error;

      const scheduledEnd = data?.periodEnd || data?.subscription_end || subscriptionEnd;
      toast({
        title: "Plan canceled",
        description: buildScheduledDowngradeMessage(scheduledEnd),
        duration: 20000,
      });

      // Force refresh subscription status and update frontend state
      await supabase.functions.invoke("check-subscription");
      await supabase.auth.refreshSession();
      await fetchUserProfile();
    } catch (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate(
          "Failed to cancel subscription. Please try again.",
        ),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;

    setSendingVerification(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: user.email,
    });

    if (error) {
      toast({
        title: AutoTranslate("Error"),
        description: AutoTranslate(
          "Failed to send verification email. Please try again.",
        ),
        variant: "destructive",
      });
    } else {
      toast({
        title: AutoTranslate("Verification Email Sent"),
        description: AutoTranslate(
          "Please check your inbox and verify your email address.",
        ),
      });
    }
    setSendingVerification(false);
  };

  const handleExportData = async () => {
    if (!user) return;

    setExportingData(true);
    try {
      // Fetch all user data
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      const { data: savedResumes } = await supabase
        .from("saved_resumes")
        .select("*")
        .eq("user_id", user.id);

      const { data: chatSessions } = await supabase
        .from("chat_sessions")
        .select("*")
        .eq("user_id", user.id);

      const tx = (text: string) => {
        const translated = AutoTranslate(text);
        return translated.startsWith("UTR:") || translated.startsWith("failed to translate:")
          ? text
          : translated;
      };
      const formatDate = (value?: string | null) =>
        value ? new Date(value).toLocaleDateString(i18n.language) : tx("Unknown");
      const getDataRightsText = () => {
        const language = i18n.language.split("-")[0];
        const translations: Record<string, string> = {
          ar: "لديك الحق في استلام هذه البيانات بصيغة منظمة وشائعة الاستخدام. إذا كانت لديك أي أسئلة أو كنت ترغب في ممارسة حقوق إضافية متعلقة بالبيانات، فيرجى التواصل عبر info@wefindservices.org.",
          zh: "您有权以结构化、常用的格式接收这些数据。如果您有任何问题，或希望行使其他数据权利，请联系 info@wefindservices.org。",
          hi: "आपको यह डेटा एक संरचित और सामान्य रूप से उपयोग किए जाने वाले प्रारूप में प्राप्त करने का अधिकार है। यदि आपके कोई प्रश्न हैं या आप अतिरिक्त डेटा अधिकारों का उपयोग करना चाहते हैं, तो कृपया info@wefindservices.org पर संपर्क करें।",
          ja: "お客様には、このデータを構造化された一般的に使用される形式で受け取る権利があります。ご質問がある場合、または追加のデータ権利を行使したい場合は、info@wefindservices.org までご連絡ください。",
          ru: "Вы имеете право получить эти данные в структурированном, широко используемом формате. Если у вас есть вопросы или вы хотите воспользоваться дополнительными правами в отношении данных, свяжитесь с нами по адресу info@wefindservices.org.",
          es: "Tiene derecho a recibir estos datos en un formato estructurado y de uso común. Si tiene alguna pregunta o desea ejercer derechos adicionales sobre sus datos, póngase en contacto con info@wefindservices.org.",
          fr: "Vous avez le droit de recevoir ces données dans un format structuré et couramment utilisé. Si vous avez des questions ou souhaitez exercer des droits supplémentaires sur vos données, veuillez contacter info@wefindservices.org.",
          de: "Sie haben das Recht, diese Daten in einem strukturierten, gängigen Format zu erhalten. Wenn Sie Fragen haben oder weitere Datenschutzrechte ausüben möchten, kontaktieren Sie bitte info@wefindservices.org.",
          sw: "Una haki ya kupokea data hii katika muundo uliopangwa na unaotumika kwa kawaida. Ikiwa una maswali yoyote au ungependa kutumia haki zaidi za data, tafadhali wasiliana na info@wefindservices.org.",
          bn: "আপনার এই ডেটা একটি কাঠামোবদ্ধ, প্রচলিতভাবে ব্যবহৃত ফরম্যাটে পাওয়ার অধিকার রয়েছে। আপনার কোনো প্রশ্ন থাকলে বা অতিরিক্ত ডেটা অধিকার প্রয়োগ করতে চাইলে info@wefindservices.org এ যোগাযোগ করুন।",
        };
        return translations[language] || "You have the right to receive this data in a structured, commonly used format. If you have any questions or wish to exercise additional data rights, please contact info@wefindservices.org.";
      };
      const isRtl = i18n.dir(i18n.language) === "rtl";
      const exportRoot = document.createElement("div");

      exportRoot.dir = isRtl ? "rtl" : "ltr";
      exportRoot.style.cssText = `
        position: absolute;
        left: -10000px;
        top: 0;
        width: 794px;
        box-sizing: border-box;
        padding: 48px;
        background: #ffffff;
        color: #111111;
        font-family: "Noto Naskh Arabic", "Noto Sans Arabic", "Noto Sans Devanagari", "Noto Sans CJK SC", "Noto Sans JP", "Microsoft YaHei", "Yu Gothic", "Meiryo", "MS Gothic", "SimSun", "Mangal", "Nirmala UI", "Arial Unicode MS", "Segoe UI", Arial, sans-serif;
        font-size: 14px;
        line-height: 1.55;
        text-align: ${isRtl ? "right" : "left"};
        white-space: normal;
      `;

      const style = document.createElement("style");
      style.textContent = `
        .data-export h1 { font-size: 28px; margin: 0 0 8px; text-align: center; }
        .data-export .generated { color: #555; margin: 0 0 28px; text-align: center; }
        .data-export h2 { font-size: 18px; margin: 28px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #dddddd; }
        .data-export ul { margin: 0; padding-inline-start: 22px; }
        .data-export li { margin: 5px 0; overflow-wrap: anywhere; }
        .data-export p { margin: 8px 0; overflow-wrap: anywhere; }
      `;
      exportRoot.className = "data-export";
      exportRoot.appendChild(style);

      const appendText = (tag: "h1" | "h2" | "p" | "li", text: string) => {
        const element = document.createElement(tag);
        element.textContent = text;
        exportRoot.appendChild(element);
        return element;
      };

      const appendList = (title: string, lines: string[]) => {
        appendText("h2", title);
        const list = document.createElement("ul");
        lines.forEach((line) => {
          const item = document.createElement("li");
          item.textContent = line;
          list.appendChild(item);
        });
        exportRoot.appendChild(list);
      };

      appendText("h1", tx("Your Personal Data Export"));
      const generated = appendText(
        "p",
        `${tx("Generated on:")} ${new Date().toLocaleDateString(i18n.language)}`,
      );
      generated.className = "generated";

      appendList(tx("Profile Information"), [
        `${tx("Email")}: ${user.email || tx("Not provided")}`,
        `${tx("Name")}: ${profile?.full_name || tx("Not provided")}`,
        `${tx("Phone Number")}: ${profile?.country_code || ""} ${profile?.phone_number || tx("Not provided")}`,
        `${tx("Job Title")}: ${profile?.job_title || tx("Not provided")}`,
        `${tx("Years Experience")}: ${profile?.years_experience || tx("Not provided")}`,
        `${tx("City")}: ${profile?.city || tx("Not provided")}`,
        `${tx("Subscription")}: ${profile?.subscription_tier || tx("starter")}`,
        `${tx("Account Created")}: ${formatDate(profile?.created_at)}`,
      ]);

      if (savedResumes && savedResumes.length > 0) {
        appendList(
          `${tx("Saved Resumes")} (${savedResumes.length})`,
          savedResumes.map(
            (resume, index) =>
              `${index + 1}. ${resume.title || tx("Untitled Resume")} - ${tx("Created:")} ${formatDate(resume.created_at)}`,
          ),
        );
      }

      if (chatSessions && chatSessions.length > 0) {
        appendList(
          `${tx("Chat Sessions")} (${chatSessions.length})`,
          chatSessions.map(
            (session, index) =>
              `${index + 1}. ${tx("Session from")} ${formatDate(session.created_at)}`,
          ),
        );
      }

      appendText("h2", tx("Data Export"));
      appendText(
        "p",
        `${tx(
          "This document contains all personal data we hold about you in accordance with Article 20 (Right to Data Portability). ",
        )} ${getDataRightsText()}`,
      );

      document.body.appendChild(exportRoot);
      let canvas: HTMLCanvasElement | null = null;
      try {
        canvas = await html2canvas(exportRoot, {
          backgroundColor: "#ffffff",
          scale: 2,
          useCORS: true,
          windowWidth: exportRoot.scrollWidth,
          windowHeight: exportRoot.scrollHeight,
        });
      } finally {
        exportRoot.remove();
      }
      if (!canvas) {
        throw new Error("Unable to render data export.");
      }

      const doc = new jsPDF("p", "pt", "a4");
      const imgData = canvas.toDataURL("image/png");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      doc.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      doc.save(
        `personal_data_export_${new Date().toISOString().split("T")[0]}.pdf`,
      );

      toast({
        title: "Data Exported",
        description: "Your personal data has been exported as a PDF file.",
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export Failed",
        description: "Failed to export your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setDeletingAccount(true);
    try {
      // Delete user data from all tables
      await supabase.from("chat_sessions").delete().eq("user_id", user.id);
      await supabase.from("chat_refreshes").delete().eq("user_id", user.id);
      await supabase.from("saved_resumes").delete().eq("user_id", user.id);
      await supabase.from("profiles").delete().eq("user_id", user.id);

      // Sign out and redirect
      await supabase.auth.signOut();

      toast({
        title: "Account Deleted",
        description: "Your account and all associated data have been deleted.",
      });

      navigate("/landing");
    } catch (error) {
      console.error("Delete account error:", error);
      toast({
        title: "Deletion Failed",
        description: "Failed to delete your account. Please contact support.",
        variant: "destructive",
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  const hasProAccess = hasActiveProAccess({
    subscription_tier: subscriptionTier,
    subscription_end: subscriptionEnd,
    subscription_canceled: subscriptionCanceled,
  });
  const subscriptionEndText = formatSubscriptionEndDate(subscriptionEnd);

  return (
    <SidebarProvider>
      <SEO
        title="Account Settings — FIND Resume Builder"
        description="Manage your FIND Resume Builder account: profile details, subscription plan, billing, data export, and notification preferences."
        path="/settings"
      />
      <div className="min-h-screen flex w-full bg-gradient-main">
        <AppSidebar />

        <main className="flex-1 flex flex-col relative z-10">
          <header className="flex items-center justify-between p-4 border-b border-border bg-card/80 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="hover:bg-primary-light" />
              <div className="flex items-center gap-2">
                <Settings className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-bold text-foreground">
                  {AutoTranslate(`Settings`)}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <HeaderOptionsPopover />
            </div>
          </header>

          <div className="flex-1 p-6 space-y-6 max-w-4xl mx-auto w-full">
            {/* My Profile Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  {AutoTranslate(`My Profile`)}
                </CardTitle>
                <CardDescription>
                  {AutoTranslate(`Manage your personal information`)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{AutoTranslate(`Email`)}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={user?.email || ""}
                      disabled
                      className="flex-1"
                    />
                    {emailVerified ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">{AutoTranslate(`Verified`)}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-destructive" />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResendVerification}
                          disabled={sendingVerification}
                        >
                          {sendingVerification ? AutoTranslate(`Sending...`) : AutoTranslate(`Verify`)}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{AutoTranslate(`Name`)}</Label>
                  <div className="flex gap-2">
                    <Input value={fullName} disabled />
                    <Dialog
                      open={showNameDialog}
                      onOpenChange={setShowNameDialog}
                    >
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4 mr-2" />
                          {AutoTranslate(`Edit`)}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{AutoTranslate(`Change Name`)}</DialogTitle>
                          <DialogDescription>
                            {AutoTranslate(`Enter your new name below`)}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder={AutoTranslate(`Enter your name`)}
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={handleNameUpdate}
                              disabled={loading || !newName.trim()}
                              className="flex-1"
                            >
                              {loading ? AutoTranslate(`Updating...`) : AutoTranslate(`Update Name`)}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setShowNameDialog(false)}
                              className="flex-1"
                            >
                              {AutoTranslate(`Cancel`)}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notifications Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  {AutoTranslate(`Notifications`)}
                </CardTitle>
                <CardDescription>
                  {AutoTranslate(
                    `Manage your email and notification preferences`,
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>{AutoTranslate(`Email Notifications`)}</Label>
                    <p className="text-sm text-muted-foreground">
                      {AutoTranslate(`Receive updates and news via email`)}
                    </p>
                  </div>
                  <Switch
                    checked={notifications}
                    onCheckedChange={handleNotificationToggle}
                  />
                </div>

                <div className="space-y-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>{AutoTranslate(`Comment Notifications`)}</Label>
                      <p className="text-sm text-muted-foreground">
                        {AutoTranslate(
                          `Get notified when someone comments on your shared resumes`,
                        )}
                      </p>
                    </div>
                    <Switch
                      checked={commentNotificationsEnabled}
                      onCheckedChange={handleCommentNotificationsToggle}
                    />
                  </div>

                  {commentNotificationsEnabled && (
                    <div className="ml-6 space-y-3">
                      <Label className="text-sm">{notificationFrequency}</Label>
                      <Select
                        value={commentNotificationFrequency}
                        onValueChange={handleCommentNotificationFrequencyChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="instant">
                            {AutoTranslate(`Instant - Receive emails immediately`)}
                          </SelectItem>
                          <SelectItem value="weekly">
                            {AutoTranslate(`Weekly Digest - Once per week`)}
                          </SelectItem>
                          <SelectItem value="monthly">
                            {AutoTranslate(`Monthly Digest - Once per month`)}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {commentNotificationFrequency === "instant" &&
                          AutoTranslate(`You will receive an email immediately when someone comments.`)}
                        {commentNotificationFrequency === "weekly" &&
                          AutoTranslate(`You will receive a weekly summary of all comments.`)}
                        {commentNotificationFrequency === "monthly" &&
                          AutoTranslate(`You will receive a monthly summary of all comments.`)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Comments Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-primary" />
                  {AutoTranslate(`Comments`)}
                </CardTitle>
                <CardDescription>
                  {AutoTranslate(`Control comment settings on your shared resumes`)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>{AutoTranslate(`Enable Comments`)}</Label>
                    <p className="text-sm text-muted-foreground">
                      {AutoTranslate(
                        `Allow others to comment on your shared resumes`,
                      )}
                    </p>
                  </div>
                  <Switch
                    checked={commentsEnabled}
                    onCheckedChange={handleCommentsEnabledToggle}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>{AutoTranslate(`Hide My Comments`)}</Label>
                    <p className="text-sm text-muted-foreground">
                      {AutoTranslate(`Hide your comments from other users`)}
                    </p>
                  </div>
                  <Switch
                    checked={commentsHidden}
                    onCheckedChange={handleCommentsHiddenToggle}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Affiliate Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Handshake className="h-5 w-5 text-primary" />
                  {AutoTranslate(`Affiliate Program`)}
                </CardTitle>
                <CardDescription>
                  {AutoTranslate(`Invite others to try out Resume Builder and earn 10% commission for the first year on all paid customers. Help others get better resumes and improve their chances with job hunting, while earning rewards.`)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  data-tally-open="mRgXEj"
                  data-tally-layout="modal"
                  data-tally-emoji-text="🎉"
                  data-tally-emoji-animation="tada"
                  data-tally-auto-close="5000"
                  data-tally-form-events-forwarding="1"
                >
                  <Handshake className="h-4 w-4 mr-2" />
                  {AutoTranslate(`Become An Affiliate`)}
                </Button>
              </CardContent>
            </Card>

            {/* Billing Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  {AutoTranslate(`Billing`)}
                </CardTitle>
                <CardDescription>
                  {AutoTranslate(`Change your plan and manage billing`)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {AutoTranslate(`Current Plan`)}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={hasProAccess ? "default" : "secondary"}>
                        {hasProAccess ? "Pro" : "Starter"}
                      </Badge>
                      {hasProAccess && (
                        <Crown className="h-4 w-4 text-yellow-500" />
                      )}
                    </div>
                    {hasProAccess && (
                      <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
                        <div className="flex items-start gap-2">
                          <CalendarDays className="mt-0.5 h-4 w-4 text-primary" />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {subscriptionCanceled
                                ? AutoTranslate(`Pro access ends`)
                                : AutoTranslate(`Next billing date`)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {subscriptionEnd
                                ? subscriptionEndText
                                : AutoTranslate(`Your end date will appear here after payment confirmation.`)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {hasProAccess && (
                    <div>
                      <p className="font-medium">{subscriptionStatus}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant={
                            subscriptionCanceled === false
                              ? "default"
                              : "secondary"
                          }
                        >
                          {subscriptionCanceled === false
                            ? AutoTranslate(`Active`)
                            : AutoTranslate(`Canceling at period end`)}
                        </Badge>
                        {subscriptionCanceled === false && (
                          <Activity className="h-4 w-4 text-green-500 ml-1" />
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {hasProAccess ? (
                      <Button
                        onClick={handleCancelSubscription}
                        variant="destructive"
                        disabled={loading || subscriptionCanceled === true}
                      >
                        {subscriptionCanceled
                          ? AutoTranslate(`Cancellation scheduled`)
                          : loading
                            ? AutoTranslate(`Canceling...`)
                            : AutoTranslate(`Cancel Plan`)}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setShowPricingDialog(true)}
                      >
                        <Crown className="h-4 w-4 mr-2" />
                        {AutoTranslate(`Upgrade to Pro`)}
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {hasProAccess
                    ? AutoTranslate(
                        `Manage your Pro subscription, update payment methods, cancel or renew your plan.`,
                      )
                    : AutoTranslate(
                        `Upgrade to Pro for unlimited resumes, saved templates, and priority support.`,
                      )}
                </p>
              </CardContent>
            </Card>

            {/* Data Privacy Section */}
            <Card className="bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  {AutoTranslate(`Data Privacy`)}
                </CardTitle>
                <CardDescription>
                  {AutoTranslate(
                    `Export your personal data or delete your account`,
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>{AutoTranslate(`Export Your Data`)}</Label>
                    <p className="text-sm text-muted-foreground">
                      {AutoTranslate(
                        `Download all your personal data as a PDF file`,
                      )}
                    </p>
                  </div>
                  <Button
                    onClick={handleExportData}
                    variant="outline"
                    disabled={exportingData}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exportingData
                      ? AutoTranslate(`Exporting...`)
                      : AutoTranslate(`Export Data`)}
                  </Button>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="text-destructive">
                        {AutoTranslate(`Delete Account`)}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {AutoTranslate(
                          `Permanently delete your account and all associated data. This action cannot be undone.`,
                        )}
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          disabled={deletingAccount}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {deletingAccount ? AutoTranslate(`Deleting...`) : AutoTranslate(`Delete Account`)}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {AutoTranslate(`Are you absolutely sure?`)}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {AutoTranslate(`This action cannot be undone. This will permanently delete your account
                            and remove all your data from our servers, including:`)}
                            <ul className="list-disc list-inside mt-2 space-y-1">
                              <li>
                                {AutoTranslate(`Your profile information`)}
                              </li>
                              <li>{AutoTranslate(`All saved resumes`)}</li>
                              <li>
                                {AutoTranslate(`Chat history and sessions`)}
                              </li>
                              <li>
                                {AutoTranslate(
                                  `Shared resume links and comments`,
                                )}
                              </li>
                            </ul>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteAccount}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {AutoTranslate(`Yes, delete my account`)}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
            <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{AutoTranslate(`Choose Your Plan`)}</DialogTitle>
              </DialogHeader>
              <Pricing onPaymentStart={() => setShowPricingDialog(false)} />
            </DialogContent>
          </Dialog>

          <Footer />
        </main>
      </div>
    </SidebarProvider>
  );
}
